import { createSelector } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "../../../shared/api/base-query";
import { filterNotDeleted } from "../../../shared/api/filter-not-deleted";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import type { QuerySubscriptionOptions } from "../../../shared/api/query-subscription-options";
import { buildFilterCondition } from "../../../shared/utils";
import { downloadResponseFile } from "../../../shared/utils/download-response-file";
import { verificationService } from "../../verification/shared/services/verification.service";
import { type ISeedsDto } from "../shared";
import { SeedsListSchema } from "../shared/dtos/seeds.schema";
import { UpdateSeedDto } from "../shared/dtos/update-seed.dto";
import { Seed } from "../shared/models/seed";

const SEEDS_LIST_TAG_TYPE = "SeedsList";
const SEEDS_LIST_TAG_ID = "LIST";
const CROP_SEED_EXCEL_FALLBACK_FILENAME = "CropSeeds.xlsx";

type SeedsListTag = {
  type: typeof SEEDS_LIST_TAG_TYPE;
  id: string;
};

function checkSeedsResponse(dtos: ISeedsDto[]): ISeedsDto[] {
  return verificationService.check({
    cacheId: "SeedsListSchema",
    schema: SeedsListSchema,
    message: "Невалидный ответ сервера (SeedsListSchema)",
  })(dtos);
}

function getCropSeedImportFormData(file: File): FormData {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
}

function getSeedsListTags(seeds: Seed[] | undefined): SeedsListTag[] {
  if (!seeds) {
    return [{ type: SEEDS_LIST_TAG_TYPE, id: SEEDS_LIST_TAG_ID }];
  }

  const seedTags: SeedsListTag[] = seeds.map((seed) => ({
    type: SEEDS_LIST_TAG_TYPE,
    id: seed.id,
  }));

  return [...seedTags, { type: SEEDS_LIST_TAG_TYPE, id: SEEDS_LIST_TAG_ID }];
}

export const seedsApiSlice = createApi({
  reducerPath: "api-seeds",
  // https://github.com/reduxjs/redux-toolkit/discussions/2347#discussioncomment-2978038
  refetchOnFocus: false,
  keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
  baseQuery,
  tagTypes: [SEEDS_LIST_TAG_TYPE, "SeedsListCount"],
  endpoints: (builder) => ({
    fetchSeedsWithDeleted: builder.query<Seed[], void>({
      queryFn: pagedQueryFn<ISeedsDto, Seed[]>(
        { url: "SeedsV2", params: { includeDeleted: true } },
        (dtos) => {
          const models: Seed[] = checkSeedsResponse(dtos).map((dto) => {
            const model = new Seed(dto.id);
            model.updateFromDto(dto);
            return model;
          });
          models.sort((seedA, seedB) => seedA.name.localeCompare(seedB.name));
          return models;
        }
      ),
      providesTags: getSeedsListTags,
    }),

    seedsByCrop: builder.query<Seed[], string>({
      queryFn: pagedQueryFn<ISeedsDto, Seed[], string>(
        { url: "SeedsV2" },
        (dtos, cropId) => {
          const normalizedCropId = cropId.toLowerCase();
          const models: Seed[] = checkSeedsResponse(dtos)
            .map((dto) => {
              const model = new Seed(dto.id);
              model.updateFromDto(dto);
              return model;
            })
            .filter((seed) => seed.cropIds.includes(normalizedCropId));
          models.sort((seedA, seedB) => seedA.name.localeCompare(seedB.name));
          return models;
        }
      ),
      providesTags: getSeedsListTags,
    }),

    seedByIdWithDeleted: builder.query<Seed | undefined, string>({
      queryFn: pagedQueryFn<ISeedsDto, Seed | undefined, string>(
        (id) => ({
          url: "SeedsV2",
          params: {
            includeDeleted: true,
            filter: buildFilterCondition("id", "==", id),
          },
        }),
        (dtos, id) => {
          const requestedId = id.toLowerCase();
          const dto = checkSeedsResponse(dtos).find(
            (seed) => seed.id.toLowerCase() === requestedId
          );
          if (!dto) return undefined;
          const model = new Seed(dto.id);
          model.updateFromDto(dto);
          return model;
        }
      ),
      providesTags: (_result, _err, id) => [{ type: SEEDS_LIST_TAG_TYPE, id }],
    }),

    updateSeed: builder.mutation<Seed, { id: string; body: UpdateSeedDto }>({
      query: ({ id, body }) => ({
        url: `SeedsV2/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (dto: ISeedsDto) => {
        const model = new Seed(dto.id);
        model.updateFromDto(dto);
        return model;
      },
      invalidatesTags: (_result, _err, { id }) => [
        { type: SEEDS_LIST_TAG_TYPE, id },
        { type: SEEDS_LIST_TAG_TYPE, id: SEEDS_LIST_TAG_ID },
      ],
    }),

    exportCropSeedExcel: builder.mutation<void, void>({
      query: () => ({
        url: "CropSeed/export",
        method: "POST",
        responseHandler: async (response) => {
          await downloadResponseFile(response, CROP_SEED_EXCEL_FALLBACK_FILENAME);
          return null;
        },
      }),
    }),

    importCropSeedExcel: builder.mutation<void, File>({
      query: (file) => ({
        url: "CropSeed/import",
        method: "POST",
        body: getCropSeedImportFormData(file),
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_result, error) => (error ? [] : [SEEDS_LIST_TAG_TYPE]),
    }),
  }),
});

const selectNotDeletedData = createSelector(
  ({ data }: { data?: Seed[] }) => data,
  filterNotDeleted<Seed>
);

const selectNotDeletedCurrentData = createSelector(
  ({ currentData }: { currentData?: Seed[] }) => currentData,
  filterNotDeleted<Seed>
);

const useSeedsEndpoint = seedsApiSlice.endpoints.fetchSeedsWithDeleted.useQuery;

type SeedsQueryOptions = QuerySubscriptionOptions<typeof useSeedsEndpoint>;

export function useFetchSeeds(options?: SeedsQueryOptions) {
  return useSeedsEndpoint(undefined, {
    ...options,
    selectFromResult: (result) => ({
      ...result,
      data: selectNotDeletedData(result),
      currentData: selectNotDeletedCurrentData(result),
    }),
  });
}

export function useFetchSeedsWithDeleted(options?: SeedsQueryOptions) {
  return useSeedsEndpoint(undefined, options);
}

export const useLazyFetchSeedsWithDeleted =
  seedsApiSlice.endpoints.fetchSeedsWithDeleted.useLazyQuery;

const useSeedsByCropEndpoint = seedsApiSlice.endpoints.seedsByCrop.useQuery;

export function useFetchSeedsByCrop(
  cropId: Parameters<typeof useSeedsByCropEndpoint>[0],
  options?: QuerySubscriptionOptions<typeof useSeedsByCropEndpoint>
) {
  return useSeedsByCropEndpoint(cropId, options);
}

const useSeedByIdEndpoint = seedsApiSlice.endpoints.seedByIdWithDeleted.useQuery;

export function useFetchSeedByIdWithDeleted(
  seedId: Parameters<typeof useSeedByIdEndpoint>[0],
  options?: QuerySubscriptionOptions<typeof useSeedByIdEndpoint>
) {
  return useSeedByIdEndpoint(seedId, options);
}

export const {
  useUpdateSeedMutation,
  useExportCropSeedExcelMutation,
  useImportCropSeedExcelMutation,
} = seedsApiSlice;
